import * as React from "react";
import styled from "styled-components";
import { MenuListSearchIcon } from "./icons/menu-list-icons";
import { MenuSortDirectionButtons } from "./menu-sort-direction-buttons";
import { canShowListSortControls, MenuListSortDirection } from "../utils/sortLists";

export type MenuListToolbarTab = {
	name: string;
	value: string;
};

const ToolbarRow = styled.div`
	align-items: center;
	display: flex;
	flex-wrap: wrap;
	gap: 16px;
	justify-content: space-between;
	margin-bottom: 24px;
	width: 100%;
`;

const TabsGroup = styled.div`
	align-items: center;
	display: flex;
	flex-shrink: 0;
	gap: 12px;
`;

const TabButton = styled.button<{ $active?: boolean }>`
	align-items: center;
	appearance: none;
	background: ${({ $active }) => ($active ? "#fff3f3" : "#fefefe")};
	border: 1px solid ${({ $active }) => ($active ? "#fb2f2f" : "#dfdfdf")};
	border-radius: 4px;
	color: ${({ $active }) => ($active ? "#fb2f2f" : "#4b4f5e")};
	cursor: pointer;
	display: flex;
	font-family: inherit;
	font-size: 14px;
	font-weight: 600;
	height: 44px;
	justify-content: center;
	line-height: 16px;
	padding: 12px 16px;
	white-space: nowrap;

	&:hover {
		border-color: ${({ $active }) => ($active ? "#fb2f2f" : "#c5c7cc")};
	}
`;

const ControlsGroup = styled.div`
	align-items: center;
	display: flex;
	flex: 1 1 280px;
	gap: 16px;
	justify-content: flex-end;
	min-width: 0;
`;

const FilterInputWrap = styled.div`
	align-items: center;
	background: #fefefe;
	border: 1px solid #d5d7da;
	border-radius: 8px;
	box-shadow: 0 1px 1px rgba(10, 13, 18, 0.05);
	display: flex;
	flex: 1;
	gap: 8px;
	height: 44px;
	max-width: 380px;
	min-width: 160px;
	padding: 12px 14px;
`;

const FilterInput = styled.input`
	appearance: none;
	background: transparent;
	border: 0;
	color: #4b4f5e;
	flex: 1;
	font-family: inherit;
	font-size: 14px;
	line-height: 24px;
	min-width: 0;
	outline: none;
	width: 100%;

	&::placeholder {
		color: #717680;
	}
`;

interface Props {
	tabs: MenuListToolbarTab[];
	activeTab: string;
	onTabChange: (value: string) => void;
	filterText: string;
	onFilterTextChange: (value: string) => void;
	filterPlaceholder: string;
	sortDirection: MenuListSortDirection;
	onSortDirectionChange: (direction: MenuListSortDirection) => void;
	showFilter?: boolean;
	showSort?: boolean;
	sortableItemCount?: number;
	controlsDisabled?: boolean;
}

export const MenuListToolbar = ({
	tabs,
	activeTab,
	onTabChange,
	filterText,
	onFilterTextChange,
	filterPlaceholder,
	sortDirection,
	onSortDirectionChange,
	showFilter = true,
	showSort = false,
	sortableItemCount = 0,
	controlsDisabled = false,
}: Props) => {
	const showSortControls = showSort && canShowListSortControls(sortableItemCount);
	const showControls = showFilter || showSortControls;

	return (
		<ToolbarRow>
			<TabsGroup>
				{tabs.map((tab) => (
					<TabButton
						key={tab.value}
						type="button"
						$active={activeTab === tab.value}
						onClick={() => onTabChange(tab.value)}
					>
						{tab.name}
					</TabButton>
				))}
			</TabsGroup>

			{showControls && (
				<ControlsGroup>
					{showFilter && (
						<FilterInputWrap>
							<MenuListSearchIcon />
							<FilterInput
								type="text"
								value={filterText}
								placeholder={filterPlaceholder}
								disabled={controlsDisabled}
								onChange={(e) => onFilterTextChange(e.target.value)}
							/>
						</FilterInputWrap>
					)}
					{showSortControls && (
						<MenuSortDirectionButtons
							sortDirection={sortDirection}
							onSortDirectionChange={onSortDirectionChange}
							disabled={controlsDisabled}
						/>
					)}
				</ControlsGroup>
			)}
		</ToolbarRow>
	);
};

